import { RushType } from '@/types/case.types';
import type { HIWorkOrderCreatePayload, HIWorkOrderUpdatePayload } from '@/pages/homeInspection/hi.schema';

export type PsWorkOrderForHiMap = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  includeHomeInspection?: boolean | null;
  documents?: Record<string, boolean | undefined> | null;
};

export type PsFormValuesForHiMap = {
  clientId?: string;
  contactId?: string;
  process?: {
    represents?: string | null;
  };
  case?: {
    fileNo?: string | null;
    number?: string | null;
    name?: string | null;
    plaintiff?: string | null;
    defendant?: string | null;
    attorneyId?: string | null;
    rush?: RushType | null;
  };
  workOrders?: PsWorkOrderForHiMap[];
};

const mapRushType = (rush: RushType | null | undefined): HIWorkOrderCreatePayload['rushType'] => {
  if (rush === RushType.SAME_DAY) return 'SAME_DAY';
  if (rush === RushType.NEXT_DAY) return 'NEXT_DAY';
  return 'NONE';
};

const nullIfEmpty = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

/**
 * Maps Process Service form state → Home Inspection create payload (OpenAPI fields only).
 * Uses the work order at `workOrderIndex` as the address source.
 * Lawyer is the case attorney's contact ID (`case.attorneyId`); the HI API requires a lawyer-contact UUID.
 *
 * Spec rename gaps (not on HI create/update OpenAPI payloads — accepted #420/#421 gap):
 * Serve By Date → Last Serve Date, Assigned Server → Inspector Name cannot be sent;
 * `HIWorkOrderCreatePayload` / `HIWorkOrderUpdatePayload` have no matching fields.
 */
export const mapPsFormToHiCreatePayload = (
  form: PsFormValuesForHiMap,
  workOrderIndex = 0
): HIWorkOrderCreatePayload => {
  const workOrder = form.workOrders?.[workOrderIndex];
  const propertyAddress = [workOrder?.address1, workOrder?.address2].filter(Boolean).join(' ').trim();

  const plaintiff = form.case?.plaintiff?.trim() ?? '';
  const defendant = form.case?.defendant?.trim() ?? '';
  const city = workOrder?.city?.trim() ?? '';
  const state = workOrder?.state?.trim() ?? '';
  const zipCode = workOrder?.zipCode?.trim() ?? '';

  return {
    serviceType: 'HOUSE_INSPECTION',
    clientId: form.clientId ?? '',
    contactId: form.contactId ?? '',
    fileNumber: nullIfEmpty(form.case?.fileNo),
    caseNumber: nullIfEmpty(form.case?.number),
    lawyer: nullIfEmpty(form.case?.attorneyId),
    represents: nullIfEmpty(form.process?.represents),
    caseName: nullIfEmpty(form.case?.name),
    rushType: mapRushType(form.case?.rush),
    mortgageCompanyOrPlaintiff: plaintiff,
    propertyAddress,
    city,
    state,
    zipCode,
    ownerOrDefendantName: defendant,
    linkedToProcessService: true,
  };
};

/**
 * Maps Process Service form state → Home Inspection update payload (OpenAPI fields only).
 * Omits `descriptionOfDocuments` so PS edit saves do not wipe HI-only document text.
 */
export const mapPsFormToHiUpdatePayload = (
  form: PsFormValuesForHiMap,
  workOrderIndex = 0
): HIWorkOrderUpdatePayload => {
  const created = mapPsFormToHiCreatePayload(form, workOrderIndex);
  return {
    contactId: created.contactId,
    lawyer: created.lawyer,
    represents: created.represents,
    mortgageCompanyOrPlaintiff: created.mortgageCompanyOrPlaintiff,
    propertyAddress: created.propertyAddress,
    city: created.city,
    state: created.state,
    zipCode: created.zipCode,
    ownerOrDefendantName: created.ownerOrDefendantName,
    rushType: created.rushType,
  };
};
